/** @jsxImportSource @emotion/react */
import { Formik, FormikConfig, FormikProps } from 'formik';
import UDFormInput from 'ud-form/components/input';
import { modalButton, modalError, modalInputStyle } from '../modals/styles';
import { Button } from 'ud-ui/components/button/styles';
import classNames from 'classnames';
import React from 'react';
import _ from 'lodash';
import { Values } from '../../../../domain/interface/form/board';
import UDFormSelect from 'ud-form/components/select';
import Row from 'modules/template-editor/ui/components/elements/components/events-selector/row';
import { CustomSelect } from 'modules/template-editor/ui/components/elements/components/events-selector/custom-select';
import * as yup from 'yup';
import { boardPresets, BoardPresets } from 'modules/template-editor/domain/consts/form/boards/boardPresets';
import UDText from '../../../../../ud-ui/components/text';
import { MainBlock } from 'modules/template-editor/ui/components/elements/components/events-selector/styles';
import { eventsOptions, typeOptions } from 'modules/template-editor/ui/components/elements/components/events-selector/type';
import ImportButton from 'modules/template-editor/ui/components/elements/components/buttons/import-button';
import Modal from 'ud-ui/components/modal';
import boardNameValidator from 'modules/template-editor/domain/validators/board-name-validator';
import boardDurationValidator from 'modules/template-editor/domain/validators/board-duration-validator';

const boardValidator = (existingNames: string[]) =>
  yup.object({
    name: yup
      .string()
      .transform((val) => (typeof val === 'string' ? val.trim() : val))
      .test('board-name', 'Invalid name', function (value) {
        const error = boardNameValidator(value ?? '', existingNames);
        if (error) {
          return this.createError({ message: error });
        }
        return true;
      }),
    duration: yup.string().when('type', {
      is: (type: any) => typeof type === 'object' && type?.value === 'events',
      then: (schema) =>
        schema.test('board-duration', 'Invalid duration', function (value) {
          const error = boardDurationValidator(value as any);
          if (error) {
            return this.createError({ message: error });
          }
          return true;
        }),
      otherwise: (schema) => schema,
    }),
  });

type IProps = FormikConfig<Values> & {
  isLoading: boolean;
  preset: BoardPresets;
  existingNames: string[];
  disableImport?: boolean;
};

function BoardForm(props: IProps) {
  const { isLoading, preset, existingNames, disableImport, ...rest } = props;

  const getErrors = (props: FormikProps<Values>) => {
    return _.find(props.errors, (error, key) => !!error && !!props?.touched[key as keyof Values]);
  };

  const buttonText = boardPresets[preset].submitButton;

  return (
    <Formik validateOnChange validationSchema={boardValidator(existingNames)} {...rest}>
      {(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit} className="w-[450px]" key={'modal-form'}>
            <Modal.Body key={'modal-content'} className="flex flex-col gap-5">
              <UDFormInput
                readOnly={isLoading}
                onChangeCapture={() => formProps.values.name && !formProps.touched.name && formProps.setFieldTouched('name')}
                name={'name'}
                autoFocus={true}
                css={modalInputStyle}
                placeholder={'Название табло'}
              />
              <MainBlock>
                <Row headerText={'Тип табло'}>
                  <UDFormSelect
                    containerProps={{ style: { width: '100%' } }}
                    selectComponent={CustomSelect}
                    name={'type'}
                    options={typeOptions}
                  />
                </Row>
                <Row headerText={'Событие'}>
                  <UDFormSelect
                    containerProps={{ style: { width: '100%' } }}
                    selectComponent={CustomSelect}
                    placeholder={'Выберите события'}
                    isDisabled={formProps.values.type?.value !== 'events'}
                    name={'events'}
                    isMulti
                    options={eventsOptions}
                  />
                </Row>
                <Row headerText={'Время'}>
                  <UDFormInput
                    containerProps={{ style: { width: '80px' } }}
                    style={{
                      background: '#F2F2F2',
                      display: 'flex',
                      textAlign: 'center',
                    }}
                    disabled={formProps.values.type?.value !== 'events' && formProps.values.type?.value !== 'carousel'}
                    name={'duration'}
                    type={'number'}
                    placeholder={'Длительность в секундах'}
                  />
                  <UDText type={'subhead'} style={{ color: '#969696' }}>
                    секунд
                  </UDText>
                </Row>
              </MainBlock>
            </Modal.Body>
            <Modal.Footer justify="start" className={classNames({ 'flex flex-col': !disableImport })}>
              <div
                className={classNames('flex items-center flex flex-row', { 'w-full': !disableImport })}
                style={{ height: 36, gap: 16 }}
              >
                <Button
                  style={{ width: 130 }}
                  type={'submit'}
                  className={classNames('flex-center', isLoading && 'loading')}
                  disabled={!formProps.isValid}
                  css={modalButton}
                >
                  {buttonText}
                </Button>
                {!disableImport && <ImportButton isLoading={isLoading} formProps={formProps} />}
              </div>
              {getErrors(formProps) && (
                <span className={classNames({ 'w-full': !disableImport })} css={modalError}>
                  {getErrors(formProps)}
                </span>
              )}
            </Modal.Footer>
          </form>
        );
      }}
    </Formik>
  );
}

export default BoardForm;
